import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { fmt } from "@/lib/format";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
  return String(detail);
}

export default function Register() {
  const [params] = useSearchParams();
  const refParam = (params.get("ref") || "").toUpperCase();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refCode, setRefCode] = useState(refParam);
  const [refInfo, setRefInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const c = (refCode || "").trim().toUpperCase();
    if (c.length >= 4) {
      api
        .get(`/referrals/validate/${c}`)
        .then(({ data }) => setRefInfo(data))
        .catch(() => setRefInfo({ valid: false }));
    } else {
      setRefInfo(null);
    }
  }, [refCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, password, name, refCode || undefined);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bp-card rounded-2xl p-8">
        <div className="overline mb-2">Join the vault</div>
        <h1 className="font-heading text-3xl text-white font-light tracking-tight mb-6">
          Create your investor account
        </h1>

        {refInfo?.valid && (
          <div className="mb-5 p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex gap-2 items-start" data-testid="referral-banner">
            <Sparkles className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="text-white">Invited by {refInfo.referrer_name}</div>
              <div className="text-xs text-gray-400 mt-0.5">
                {refInfo.referee_reward > 0 ? (
                  <>You'll receive a <span className="text-[#D4AF37]">{fmt(refInfo.referee_reward)}</span> welcome bonus after your first purchase.</>
                ) : (
                  <>Join the most trusted fractional real-estate platform in Nigeria.</>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-gray-300">Full name</Label>
            <Input
              data-testid="register-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
          </div>
          <div>
            <Label className="text-gray-300">Email</Label>
            <Input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
          </div>
          <div>
            <Label className="text-gray-300">Password</Label>
            <Input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
          </div>
          <div>
            <Label className="text-gray-300">Referral code <span className="text-gray-500 text-xs">(optional)</span></Label>
            <Input
              data-testid="register-referral"
              value={refCode}
              onChange={(e) => setRefCode(e.target.value.toUpperCase())}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37] font-mono tracking-widest"
              placeholder="E.G. 7XK9A2QP"
            />
            {refCode && refInfo && !refInfo.valid && (
              <div className="text-xs text-red-400 mt-1">Invalid referral code</div>
            )}
          </div>
          <Button
            type="submit"
            data-testid="register-submit"
            disabled={busy}
            className="w-full btn-gold rounded-full h-11"
          >
            {busy ? "Creating..." : "Create account"}
          </Button>
        </form>
        <div className="text-center text-sm text-gray-400 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="link-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
